import { useTranslation } from 'react-i18next';
import React, { ReactComponentElement, useCallback, useEffect, useState } from 'react';
import { ArrowAngleDown, ArrowAngleUp } from '@zooplus/icons';
import { Anchor, ShadowBox } from '@zooplus/ui-components';
import classNames from 'classnames';
import Loader from '../../loader/Loader';
import { format as formatDate } from '../../../utils/DateUtils';
import { OrderArticlesPictures } from './OrderArticlesPictures';
import { usePaginatedOrders } from './hooks/usePaginatedOrders';
import { OrderSellerInformation } from './OrderSellerInformation';

const ORDER_SELECTOR_OPTION_SELECTOR_PAGE_SIZE: number = 5;

type OrderSelectorOptionSelectorProps = {
  isOpen: boolean;
  selectOrderId: (orderId: string, suborderId: string) => void;
  selectedSuborder: string;
  onError: () => void;
  filterValue: string;
};

export const OrderSelectorOptionSelector = (props: OrderSelectorOptionSelectorProps) => {
  const { isOpen, selectOrderId, onError } = props;
  const { t } = useTranslation();
  const [isPagingDisabled, setIsPagingDisabled] = useState(false);
  const { orders, isLoading, hasMoreOrders, fetchNextPage } = usePaginatedOrders(onError);
  const [visiblePage, setVisiblePage] = useState<number>(0);
  const ordersForCurrentPage = orders.slice(
    visiblePage * ORDER_SELECTOR_OPTION_SELECTOR_PAGE_SIZE,
    (visiblePage + 1) * ORDER_SELECTOR_OPTION_SELECTOR_PAGE_SIZE,
  );
  const filteredOrders = props.filterValue
    ? orders.filter((order) => order.suborderId.startsWith(props.filterValue))
    : ordersForCurrentPage;

  const isFirstPage = visiblePage === 0;
  const hasNextPage =
    hasMoreOrders || (visiblePage + 1) * ORDER_SELECTOR_OPTION_SELECTOR_PAGE_SIZE < orders.length;

  const loadNextPage = useCallback(() => {
    if (isLoading) {
      return;
    }
    if (
      hasMoreOrders &&
      orders.length < (visiblePage + 2) * ORDER_SELECTOR_OPTION_SELECTOR_PAGE_SIZE
    ) {
      fetchNextPage().then(() => setVisiblePage(visiblePage + 1));
    } else {
      setVisiblePage(visiblePage + 1);
    }
  }, [visiblePage, isLoading]);

  const loadPreviousPage = useCallback(
    () => setVisiblePage((prevValue) => prevValue && prevValue - 1),
    [],
  );

  const loadedAndFilteredOrdersCountIsGreaterThenZero =
    !isLoading && (!props.filterValue || filteredOrders.length > 0);
  const isVisible = isOpen && (isLoading || loadedAndFilteredOrdersCountIsGreaterThenZero);

  useEffect(() => {
    setIsPagingDisabled(!!props.filterValue);
  }, [props.filterValue]);

  const onSelectItem = (orderId: string, suborderId: string, e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault(); // emit no event so that the input does NOT get blurred
    selectOrderId(orderId, suborderId);
  };

  return (
    <ShadowBox
      className={classNames('orderSelector__optionSelector', {
        'orderSelector__optionSelector--invisible': !isVisible,
      })}
      data-test="orderSelector.optionSelector">
      <OrderSelectorButton
        renderIcon={(size: number) => <ArrowAngleUp width={size} height={size} />}
        onClick={loadPreviousPage}
        isDisabled={isFirstPage || isPagingDisabled}
        className="orderSelector__pageSelectorPreviousPage"
        text={t('form.fields.order_number.order_selector.order_list_previous')}
        dataTest="orderSelector.optionSelector.previousPageButton"
      />
      <div className={'orderSelector__listWrapper'}>
        <Loader isLoading={isLoading} variant={'zooplus'}>
          <ul data-test="orderSelector.optionSelector.list">
            {orders.map((order) => (
              <li
                key={order.suborderId}
                data-order-id={order.suborderId}
                className={classNames(
                  'orderSelector__optionSelectorItem',
                  {
                    'orderSelector__optionSelectorItem--visible': filteredOrders.includes(order),
                  },
                  { 'orderSelector__optionSelectorItem--firstItem': order === filteredOrders[0] },
                  {
                    'orderSelector__optionSelectorItem--selected':
                      order.suborderId === props.selectedSuborder,
                  },
                )}
                onMouseDown={onSelectItem.bind(null, order.orderId, order.suborderId)}>
                <div>
                  <strong>
                    {t('form.fields.order_number.order_selector.order_date_header')}{' '}
                    <span data-test="orderSelector.optionSelector.list.creationDate">
                      {formatDate(order.creationDateIsoString)}
                    </span>
                  </strong>
                  <span
                    className={'orderSelector__optionSelectorItemOrderId'}
                    data-test="orderSelector.optionSelector.list.orderId">
                    #{order.suborderId}
                  </span>
                </div>
                <OrderSellerInformation order={order} />
                <OrderArticlesPictures order={order} />
              </li>
            ))}
          </ul>
        </Loader>
      </div>
      <OrderSelectorButton
        renderIcon={(size: number) => <ArrowAngleDown width={size} height={size} />}
        onClick={loadNextPage}
        isDisabled={isLoading || (!isLoading && !hasNextPage) || isPagingDisabled}
        className="orderSelector__pageSelectorNextPage"
        text={t('form.fields.order_number.order_selector.order_list_next')}
        dataTest="orderSelector.optionSelector.nextPageButton"
      />
    </ShadowBox>
  );
};

OrderSelectorOptionSelector.defaultProps = {
  filterValue: '',
};

type OrderSelectorButtonProps = {
  renderIcon: (size: number) => ReactComponentElement<any>;
  onClick: () => void;
  isDisabled: boolean;
  className: string;
  text: string;
  dataTest: string;
};

const OrderSelectorButton = (props: OrderSelectorButtonProps) => {
  const iconSize = 16;
  return (
    <div
      onClick={props.onClick}
      className={classNames(
        'orderSelector__pageSelector',
        { 'orderSelector__pageSelector--disabled': props.isDisabled },
        props.className,
      )}
      data-test={props.dataTest}>
      <Anchor isBold={true} isDisabled={props.isDisabled}>
        <div>{props.text}</div>
        <div className="pp-ml-2xs orderSelector__iconWrapper">{props.renderIcon(iconSize)}</div>
      </Anchor>
    </div>
  );
};
